import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { useUploadContext } from '@components/Files/UploadProvider';
import { jobIsScheduled } from '@helpers/jobStatus/jobIsScheduled';
import { useAPI } from '@providers/useAPI';
import { usePageContext } from '@pages/User/NewJobPage/providers/PageProvider';
import { useSaveJob } from '@pages/User/NewJobPage/hooks/useSaveJob';
import { useAuthContext } from '@providers/AuthProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { JobsService } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { ButtonsWrapper } from './styles/ButtonsWrapper';
import { routes } from '@routes';
import {
  getNewJobFileDuration,
  getNewJobFilesSubmitReadiness,
} from '@pages/User/NewJobPage/helpers/getNewJobFilesSubmitReadiness';

type TProps = {
  filesLength: number;
  saveFilesOrder: () => void;
  errorJobNameMessage: string;
  noticeExtractionRunning: boolean;
  attachmentUploadRunning: boolean;
};

export const Buttons = ({
  filesLength,
  saveFilesOrder,
  errorJobNameMessage,
  noticeExtractionRunning,
  attachmentUploadRunning,
}: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { files } = useUploadContext();
  const { isLoading, form, isNew, job } = usePageContext();
  const navigate = useNavigate();
  const { workspace, organization } = useAuthContext();

  const saveJob = useSaveJob(saveFilesOrder);

  const addAttorneyAccessMutation = useMutation({
    mutationFn: (request: Parameters<typeof JobsService.addNewAttorneyAccess>[0]) =>
      call(() => JobsService.addNewAttorneyAccess(request)),
    onError: notifyApiCallError,
  });

  const removeDraftJobMutation = useMutation({
    mutationFn: (request: Parameters<typeof JobsService.removeDraftJob>[0]) =>
      call(() => JobsService.removeDraftJob(request)),
    onError: notifyApiCallError,
    onSettled: () => queryClient.invalidateQueries({ queryKey: jobsKeys.lists() }),
  });

  const goBack = useCallback(() => {
    if (!workspace || !organization) {
      return;
    }
    navigate(routes.jobsList.make(organization.slug, workspace.slug));
  }, [navigate, organization, workspace]);

  const activeFiles = files.filter((f) => f.meta.status !== 'removed');
  const isUploading = files.some((f) => f.meta.status === 'uploading' || f.meta.status === 'preparing');
  const noBillingAccount = !organization?.billingAccount?.id;
  const { allFilesValid, allHaveDuration } = getNewJobFilesSubmitReadiness(files);
  const isThereBadFiles = !!activeFiles.find(
    (f) =>
      (f.meta.status === 'done' &&
        f.meta.extra?.analysisStatus === 'done' &&
        !Number.isFinite(getNewJobFileDuration(f))) ||
      (f.meta.status === 'done' && f.meta.extra?.analysisStatus === 'done' && getNewJobFileDuration(f) === 0) ||
      f.meta.extra?.analysisStatus === 'error',
  );
  const isThereNotReadyFileStatuses = !allFilesValid || !allHaveDuration;
  const isThereJobNameError = !!errorJobNameMessage;

  const isDurationMoreThan10Hrs = (() => {
    if (!activeFiles.length) {
      return false;
    }
    const allDone = activeFiles.every((f) => f.meta.status === 'done' || f.meta.status === 'external');
    const allHaveDuration = activeFiles.every(
      (f) => Number.isFinite(getNewJobFileDuration(f)) && getNewJobFileDuration(f) > 0,
    );
    if (!allDone || !allHaveDuration) {
      return false;
    }
    const isMixdown = (form.item?.multipleFilesHandling ?? 'concat') === 'mixdown';
    const durations = activeFiles.map(getNewJobFileDuration);
    const total = durations.reduce((s, d) => s + d, 0);
    const limit = isMixdown ? Math.max(...durations) : total;
    return limit > 10 * 60 * 60;
  })();

  const disableCondition =
    form.item?.name === 'Unnamed' ||
    !form.item?.name ||
    isThereBadFiles ||
    isThereNotReadyFileStatuses ||
    isLoading ||
    isUploading ||
    attachmentUploadRunning ||
    noticeExtractionRunning ||
    !filesLength ||
    noBillingAccount ||
    isThereJobNameError ||
    isDurationMoreThan10Hrs;
  const draftDisableCondition =
    form.item?.name === 'Unnamed' ||
    !form.item?.name ||
    isThereJobNameError ||
    isThereBadFiles ||
    isThereNotReadyFileStatuses ||
    isLoading ||
    isUploading ||
    attachmentUploadRunning ||
    isDurationMoreThan10Hrs;

  const meetingTypeCondition =
    (form.item?.meetingType ?? '') === 'RECORDING_SESSION_REMOTE'
      ? !!form.item?.meetingLink
      : (form.item?.meetingType ?? '') === 'RECORDING_SESSION_HYBRID'
        ? true
        : !!form.item?.meetingAddress;
  // ? !!form.item?.meetingLink && !!form.item?.meetingAddress
  const scheduleDisableCondition =
    form.item?.name === 'Unnamed' ||
    !form.item?.name ||
    isThereBadFiles ||
    isThereNotReadyFileStatuses ||
    isLoading ||
    isUploading ||
    attachmentUploadRunning ||
    noticeExtractionRunning ||
    !form.item?.meetingType ||
    !form.item?.meetingDate ||
    !form.item?.meetingTime ||
    !form.item?.meetingTimezone ||
    !form.item?.meetingAssigneeId ||
    !meetingTypeCondition;

  const handleDraft = async () => {
    const result = await saveJob('JOB_STATUS_DRAFT');
    if (result) {
      goBack();
    }
  };

  const onSchedule = async () => {
    const result = await saveJob('JOB_STATUS_SCHEDULED');
    if (!result || !workspace) {
      return;
    }
    const recordingSessionId = result.meetingId;
    if (isNew && job?.attorneyAccesses?.length && recordingSessionId) {
      for (const attorney of job.attorneyAccesses) {
        addAttorneyAccessMutation.mutate({
          wsid: workspace.id,
          jobId: form.item.id,
          email: attorney.email ?? '',
          recordingSessionId,
        });
      }
    }
    goBack();
  };

  const onCancel = async () => {
    if (!workspace?.id || !form.item?.id) {
      return;
    }
    if (isNew && job?.id) {
      try {
        await removeDraftJobMutation.mutateAsync({ wsid: workspace.id, jobId: job.id });
      } catch {
        return;
      }
    }
    goBack();
  };

  return (
    <ButtonsWrapper>
      <div className="leftSide">
        {form.item?.status && jobIsScheduled(form.item?.status) ? null : (
          <>
            <Button disabled={!organization?.permissions?.submitJobs || disableCondition} type="submit">
              Calculate Price
            </Button>

            <BoldButton onClick={handleDraft} type="button" disabled={draftDisableCondition}>
              Save Draft
            </BoldButton>
          </>
        )}

        {form.item?.status && !jobIsScheduled(form.item?.status) ? null : (
          <BoldButton onClick={onSchedule} type="button" disabled={scheduleDisableCondition}>
            Schedule
          </BoldButton>
        )}
      </div>
      <div className="rightSide">
        <OutlinedButton
          onClick={onCancel}
          type="button"
          disabled={isLoading || isUploading || removeDraftJobMutation.isPending || !form.item?.id || !workspace?.id}
        >
          Cancel
        </OutlinedButton>
      </div>
    </ButtonsWrapper>
  );
};
